import { PriceType } from '@shared/utils/History/CashItemUtils';
import { Color } from '../../../Graphics';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterBoolean } from '../InputParamaterClasses/InputParameterBoolean';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
var DisplayMode;
(function (DisplayMode) {
    DisplayMode[DisplayMode["HVAndPercentile"] = 0] = "HVAndPercentile";
    DisplayMode[DisplayMode["PercentileOnly"] = 1] = "PercentileOnly";
})(DisplayMode || (DisplayMode = {}));
export class HV extends IndicatorScriptBase {
    constructor() {
        super();
        this._hvCollection = [];
        this._cursorPosition = null;
        this.ProjectName = 'Historical Volatility';
        this.SetIndicatorLine('HV', Color.Red, 2, LineStyle.SimpleChart);
        this.SeparateWindow = true;
        this.STDPeriod = 30;
        super.InputParameter(new InputParameterInteger('STDPeriod', 'STD Period', 0, 2, Number.MAX_SAFE_INTEGER));
        this.VolatilityPeriod = 365;
        super.InputParameter(new InputParameterInteger('VolatilityPeriod', 'Volatility Period', 1, 1, Number.MAX_SAFE_INTEGER));
        this.SourcePrice = PriceType.Close;
        super.InputParameter(new InputParameterCombobox('SourcePrice', 'Sources prices for HV', 2, [
            { Close: PriceType.Close },
            { Open: PriceType.Open },
            { High: PriceType.High },
            { Low: PriceType.Low },
            { Typical: PriceType.Typical },
            { Medium: PriceType.Medium },
            { Weighted: PriceType.Weighted }
        ]));
        this.isAllHistoryMode = true;
        super.InputParameter(new InputParameterBoolean('isAllHistoryMode', 'All history', 3));
        this.PercentilePeriod = 100;
        super.InputParameter(new InputParameterInteger('PercentilePeriod', 'History period', 4, 0, 9999));
        this.DisplayMode = DisplayMode.HVAndPercentile;
        super.InputParameter(new InputParameterCombobox('DisplayMode', 'Display mode', 5, [
            { 'HV and percentile': DisplayMode.HVAndPercentile },
            { 'Only percentile': DisplayMode.PercentileOnly }
        ]));
    }
    Init() {
        super.IndicatorShortName(this.GetIndicatorShortName());
        this._hvCollection = [];
    }
    GetIndicatorShortName() {
        return `HV (${this.STDPeriod}; ${this.DisplayMode === DisplayMode.HVAndPercentile ? 'HV and percentile' : 'Only percentile'})`;
    }
    Complete() {
        this._hvCollection = null;
    }
    NextBar() {
        if (this.CurrentData.Count < this.STDPeriod)
            return;
        const hv = this.GetStandardDeviation(this.STDPeriod) * Math.sqrt(this.VolatilityPeriod) * 100;
        this._hvCollection.unshift(hv);
        if (this.IsHistoryLoaded() && this.DisplayMode === DisplayMode.PercentileOnly) {
            for (let i = 0; i < this._hvCollection.length; i++) {
                const percentile = this.GetPercentile(i, this._hvCollection[i]);
                this.SetValue(0, i, percentile);
            }
        }
    }
    OnQuote() {
        if (this.CurrentData.Count < this.STDPeriod)
            return;
        const hv = this.GetStandardDeviation(this.STDPeriod) * Math.sqrt(this.VolatilityPeriod) * 100;
        this._hvCollection[0] = hv;
        if (this.DisplayMode === DisplayMode.HVAndPercentile) {
            this.SetValue(0, 0, hv);
        }
        else if (this.DisplayMode === DisplayMode.PercentileOnly) {
            this.SetValue(0, 0, this.GetPercentile(0, hv));
        }
    }
    GetStandardDeviation(period) {
        const data = Array.from({ length: period - 1 }, (_, i) => Math.log(this.CurrentData.getPriceByType(this.SourcePrice, i) /
            this.CurrentData.getPriceByType(this.SourcePrice, i + 1)));
        const sumOfDerivationAverage = data.reduce((sum, value) => sum + value * value, 0) / (period - 1);
        const average = data.reduce((sum, value) => sum + value, 0) / data.length;
        return Math.sqrt(sumOfDerivationAverage - average * average);
    }
    processMouseMove(chart) {
        this._cursorPosition = chart.CursorPosition;
        return false;
    }
    onPaintChart(chart, params) {
        const line1Color = this.Values['line1.Color'];
        if (line1Color !== this.indicatorColor) {
            this.indicatorColor = line1Color;
        }
        if (this._cursorPosition != null) {
            const time = params.window.PointsConverter.GetDataX(this._cursorPosition.X);
            const offset = Math.round(chart.FindIntervalExactly(time));
            if (offset <= this.CurrentData.Count - 1) {
                const targetIndex = this.CurrentData.Count - offset - 1;
                const hv = this._hvCollection[targetIndex];
                const text = `Percentile: ${this.GetPercentile(targetIndex, hv).toFixed(2)}%`;
                const textWidth = params.gr.measureText(text).width;
                params.gr.font = '24px';
                params.gr.fillStyle = this.indicatorColor;
                params.gr.fillText(text, params.window.ClientRectangle.X + textWidth / 2, params.window.ClientRectangle.Y + 15, textWidth);
            }
        }
    }
    GetPercentile(targetIndex, value) {
        let percentile;
        if (this.isAllHistoryMode) {
            percentile = this.CalcPercentileInRange(this._hvCollection.length - 1, 0, value);
        }
        else {
            const startIndex = Math.min(targetIndex + this.PercentilePeriod, this._hvCollection.length - 1);
            percentile = this.CalcPercentileInRange(startIndex, targetIndex, value);
        }
        return percentile;
    }
    CalcPercentileInRange(startIndex, endIndex, value) {
        let count = 0;
        for (let i = startIndex; i > endIndex; i--) {
            if (this._hvCollection[i] <= value)
                count++;
        }
        return (count / (startIndex - endIndex)) * 100;
    }
    IsHistoryLoaded() {
        const dt = new Date();
        const index = Math.round(this.CurrentData.FindInterval(dt));
        return index === 0;
    }
}
